/**
 * Módulo: Navbar
 * Sticky shadow, dropdown desktop, cascade hover, overlay mobile com drill-down 3 níveis, scroll suave.
 */

export function initNavbar() {
  const navbar = document.querySelector(".navbar");
  if (!navbar) return;

  const toggle        = navbar.querySelector(".navbar__toggle");
  const overlay       = document.querySelector(".navbar__overlay");
  const mainPanel     = document.querySelector(".navbar__panel--main");
  const dropdownItems = navbar.querySelectorAll(".navbar__item--has-dropdown");
  const scrollLinks   = document.querySelectorAll('[data-scroll="true"]');

  // 1. Sticky shadow
  window.addEventListener(
    "scroll",
    () => navbar.classList.toggle("navbar--scrolled", window.scrollY > 0),
    { passive: true }
  );

  // 2. Dropdown desktop — abre/fecha por click
  dropdownItems.forEach((item) => {
    const trigger = item.querySelector(".navbar__item-link");
    if (!trigger) return;

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      const isOpen = item.classList.contains("is-open");
      closeAllDropdowns();
      if (!isOpen) {
        item.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }
    });
  });

  // 2.1 Seletor de idioma (desktop) — mesmo padrão de click dos dropdowns
  const langSwitcher = navbar.querySelector(".navbar__lang--desktop");
  const langToggle   = langSwitcher?.querySelector(".navbar__lang-toggle");

  langToggle?.addEventListener("click", (e) => {
    e.stopPropagation();
    const isOpen = langSwitcher.classList.contains("is-open");
    closeAllDropdowns();
    closeLangMenu();
    if (!isOpen) {
      langSwitcher.classList.add("is-open");
      langToggle.setAttribute("aria-expanded", "true");
    }
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".navbar__lang--desktop")) closeLangMenu();
    if (e.target.closest(".navbar__item--has-dropdown")) return;
    closeAllDropdowns();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closeAllDropdowns();
    if (langSwitcher?.classList.contains("is-open")) {
      closeLangMenu();
      langToggle?.focus();
    }
    if (overlay?.classList.contains("is-open")) {
      closeOverlay();
      toggle?.focus();
    }
  });

  // 3. Toggle mobile
  toggle?.addEventListener("click", () => {
    if (!overlay) return;
    const isOpen = overlay.classList.toggle("is-open");
    toggle.setAttribute("aria-expanded", String(isOpen));
    document.body.style.overflow = isOpen ? "hidden" : "";

    if (isOpen) {
      mainPanel
        ?.querySelector(".navbar__overlay-link, .navbar__overlay-trigger")
        ?.focus();
    }
  });

  // 4. Fechar overlay — todos os botões ✕
  document.querySelectorAll(".navbar__overlay-close").forEach((btn) => {
    btn.addEventListener("click", () => {
      closeAllSubPanels();
      closeOverlay();
      toggle?.focus();
    });
  });

  // 5. Abrir sub-painel — funciona para todos os níveis (2 e 3)
  document.querySelectorAll(".navbar__overlay-trigger").forEach((trigger) => {
    const panelId = trigger.dataset.opensPanel;
    if (!panelId) return;

    trigger.addEventListener("click", () => {
      const subPanel = document.querySelector(`[data-panel="${panelId}"]`);
      if (!subPanel) return;

      // Esconde o painel atual (main ou sub de nível superior)
      const parentPanel = trigger.closest("[data-panel]");
      if (parentPanel === mainPanel) {
        mainPanel.classList.add("is-hidden");
      } else if (parentPanel) {
        parentPanel.classList.remove("is-active");
      } else {
        mainPanel?.classList.add("is-hidden");
      }

      subPanel.classList.add("is-active");
      trigger.setAttribute("aria-expanded", "true");

      subPanel
        .querySelector(".navbar__panel-back, .navbar__overlay-link, .navbar__overlay-trigger")
        ?.focus();
    });
  });

  // 6. Botão Voltar — respeita data-back-to para drill-down multi-nível
  document.querySelectorAll(".navbar__panel-back").forEach((btn) => {
    btn.addEventListener("click", () => {
      const backTo      = btn.dataset.backTo || "main";
      const currentPanel = btn.closest("[data-panel]");
      currentPanel?.classList.remove("is-active");

      if (backTo === "main") {
        // Fecha todos os sub-painéis e volta ao main
        document
          .querySelectorAll(".navbar__panel--sub")
          .forEach((p) => p.classList.remove("is-active"));
        mainPanel?.classList.remove("is-hidden");
        mainPanel
          ?.querySelector(".navbar__overlay-link, .navbar__overlay-trigger")
          ?.focus();
      } else {
        // Volta para o painel pai específico
        const targetPanel = document.querySelector(`[data-panel="${backTo}"]`);
        targetPanel?.classList.add("is-active");
        targetPanel
          ?.querySelector(".navbar__panel-back, .navbar__overlay-link, .navbar__overlay-trigger")
          ?.focus();
      }
    });
  });

  // 7. Scroll suave para âncoras
  scrollLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (!href?.startsWith("#")) return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      const overlayIsOpen = overlay?.classList.contains("is-open");
      closeAllSubPanels();
      closeOverlay();

      setTimeout(
        () => target.scrollIntoView({ behavior: "smooth" }),
        overlayIsOpen ? 350 : 0
      );
    });
  });

  // 8. Cascade desktop — clique em col1 abre col2, clique em col2 abre col3
  document.querySelectorAll(".navbar__cascade").forEach((cascade) => {
    const col1Btns = cascade.querySelectorAll("[data-opens-col2]");

    // Impede que cliques dentro do cascade fechem o dropdown via document listener
    cascade.addEventListener("click", (e) => e.stopPropagation());

    function activateCol2(btn) {
      const col2Id = btn.dataset.opensCol2;

      col1Btns.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");

      cascade
        .querySelectorAll(".navbar__cascade-col2 .navbar__cascade-panel")
        .forEach((p) => p.classList.remove("is-active"));
      cascade
        .querySelectorAll(".navbar__cascade-col3 .navbar__cascade-panel")
        .forEach((p) => p.classList.remove("is-active"));
      cascade
        .querySelectorAll("[data-opens-col3]")
        .forEach((b) => b.classList.remove("is-active"));

      const col2Panel = cascade.querySelector(`#${col2Id}`);
      if (col2Panel) col2Panel.classList.add("is-active");
    }

    function activateCol3(btn) {
      const col3Id      = btn.dataset.opensCol3;
      const parentPanel = btn.closest(".navbar__cascade-panel");

      parentPanel
        ?.querySelectorAll("[data-opens-col3]")
        .forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");

      cascade
        .querySelectorAll(".navbar__cascade-col3 .navbar__cascade-panel")
        .forEach((p) => p.classList.remove("is-active"));
      cascade.querySelector(`#${col3Id}`)?.classList.add("is-active");
    }

    col1Btns.forEach((btn) =>
      btn.addEventListener("click", () => activateCol2(btn))
    );

    cascade.querySelectorAll("[data-opens-col3]").forEach((btn) =>
      btn.addEventListener("click", () => activateCol3(btn))
    );
  });

  // ── Helpers ──────────────────────────────────

  function closeAllDropdowns() {
    dropdownItems.forEach((item) => {
      item.classList.remove("is-open");
      item
        .querySelector(".navbar__item-link")
        ?.setAttribute("aria-expanded", "false");
    });
  }

  function closeLangMenu() {
    langSwitcher?.classList.remove("is-open");
    langToggle?.setAttribute("aria-expanded", "false");
  }

  function closeAllSubPanels() {
    document.querySelectorAll(".navbar__panel--sub").forEach((p) => {
      const panelId = p.dataset.panel;
      p.classList.remove("is-active");
      document
        .querySelector(`[data-opens-panel="${panelId}"]`)
        ?.setAttribute("aria-expanded", "false");
    });
    mainPanel?.classList.remove("is-hidden");
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.classList.remove("is-open");
    toggle?.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
  }
}
